/**
 * renderLogText — split an event-log line into React children, drawing each
 * `[n]` die token (see gameLog.dieToken) as a filled numeral tile.
 *
 * EVERY die is a tile. The Unicode pip faces used to cover one to six, but they
 * stop there, so a custom-dice border — a Capture the Flag tower rolling d9 — fell
 * back to a bare digit leaning against two pip glyphs. Tiling just the big faces
 * then left a single roll reading in two visual languages, with two sets of font
 * metrics to keep vertically aligned against each other. One language for every
 * face is simpler in every direction, and a die's job in a log line is to say which
 * number came up.
 *
 * Used by RecentActivitySidebar, RecentActivityRollup, the full log screen and the
 * in-turn attack log — every place a formatted GameEvent string lands in the UI, so
 * one definition here reaches all of them.
 */

import React from 'react'
import { PixelRatio, Platform, Text } from 'react-native'
import Svg, { G, Rect, Text as SvgText } from 'react-native-svg'
import { PLAYER_COLORS } from '@warcarp/ui'
import { playerColorSlot } from './playerColor'
import type { GamePlayerResponse } from '@warcarp/api-types'
import type { EventRow } from './gameLog'

// A die token: `[9]`, emitted by gameLog.dieToken.
const LOG_DIE_RE = /\[(\d{1,2})\]/g

/**
 * Tile fill for a die face. A flat translucent neutral rather than a
 * theme colour: this module is outside the Tailwind `content` globs (which cover
 * app/ and components/, not lib/), so a NativeWind className here would generate
 * no styles at all. Translucency is what makes one value work on both the cream
 * and the bunker-dark themes, and the numeral itself INHERITS the row's ink
 * colour from the parent <Text>, so it is always the readable one.
 */
const DIE_TILE_FILL = 'rgba(122, 116, 86, 0.32)'

/**
 * Gap between consecutive tiles, as a plain (background-less) string. Without it
 * `[9][9][6]` renders as three touching background spans, which read as one long
 * rounded pill rather than as three dice.
 */
const TILE_GAP = '\u2009'

/**
 * Tile geometry, all fractions of the row's `diceSize` so the tiles scale with
 * whatever size the surface asks for.
 *
 * None of the three paddings match, and each difference is doing a job:
 *
 *   H vs V — a digit's glyph box is far taller than it is wide, so even padding on
 *            four sides produces a tall pill. The horizontal help is what makes the
 *            fill come out square.
 *   TOP vs BOTTOM — the background of an inline box covers the font's whole
 *            ascent-to-descent range, not the glyph, so where a digit LOOKS centred
 *            depends on the face. Measured out of the shipped Zilla Slab (per 1000
 *            units/em): ascent 944, descent 256, cap height 650. A lining digit runs
 *            baseline→cap, so it has 944−650 = 294 of empty room above it and 256
 *            below — it sits slightly LOW, and the correction is a hair on the
 *            BOTTOM, not the top. (An earlier version assumed the opposite and
 *            padded 120 onto the top, which pushed the digits down.)
 *
 * Native text spans don't provide box padding or rounded backgrounds. Native
 * tiles therefore use inline SVG attachments with the same horizontal padding.
 */
const TILE_FONT = 0.55
const TILE_PAD_H = 0.42
const TILE_PAD_TOP = 0.1
/** Top padding plus the 0.038 em by which this face's digits sit low. */
const TILE_PAD_BOTTOM = 0.14
const TILE_RADIUS = 0.22

export interface NameHighlight {
  /** The exact substring to colour (e.g. a player display name). */
  text: string
  /** CSS hex colour for the wrapped Text (typically a PLAYER_COLORS hex). */
  color: string
}

/**
 * Build the highlights array for an EventRow: the row's primary actor
 * (attacker / player) and, for combat rows, the defender — each tagged
 * with their slot's PLAYER_COLORS hex so the names render in their
 * board colour inline.
 *
 * Returns [] when none of the slot lookups resolves (lobby preview /
 * orphan event), so renderLogText falls through to its plain path.
 */
export function highlightsForRow(
  row: Pick<EventRow, 'playerId' | 'defenderId'>,
  playerBySlot: Map<string, GamePlayerResponse>,
): NameHighlight[] {
  const out: NameHighlight[] = []
  const actor = playerBySlot.get(row.playerId)
  if (actor) {
    out.push({
      text: actor.displayName,
      color: PLAYER_COLORS[playerColorSlot(actor)]?.hex ?? '#888',
    })
  }
  if (row.defenderId && row.defenderId !== '') {
    const defender = playerBySlot.get(row.defenderId)
    if (defender) {
      out.push({
        text: defender.displayName,
        color: PLAYER_COLORS[playerColorSlot(defender)]?.hex ?? '#888',
      })
    }
  }
  return out
}

/**
 * The board colours for a row's dots — the actor and (combat) defender — each
 * resolved from the player OBJECT's chosen `colorSlot`, so the dots match the
 * inline name-highlight colours exactly (see highlightsForRow). This deliberately
 * resolves from the roster object rather than the seat→colour context so the log
 * renders the right colour even outside a PlayerColorProvider. `null` when the
 * player isn't in the roster (orphan event / lobby preview) → render no dot.
 */
export function dotColorsForRow(
  row: Pick<EventRow, 'playerId' | 'defenderId'>,
  playerBySlot: Map<string, GamePlayerResponse>,
): { actor: string | null; defender: string | null } {
  const actor = playerBySlot.get(row.playerId)
  const defenderRow =
    row.defenderId && row.defenderId !== '' ? playerBySlot.get(row.defenderId) : undefined
  return {
    actor: actor ? PLAYER_COLORS[playerColorSlot(actor)]?.hex ?? '#888' : null,
    defender: defenderRow ? PLAYER_COLORS[playerColorSlot(defenderRow)]?.hex ?? '#888' : null,
  }
}

function nativeDieGeometry(value: string, diceSize: number, fontOverride?: number) {
  const font = fontOverride ?? Math.max(8, Math.round(diceSize * TILE_FONT))
  const padding = Math.max(2, Math.round(font * TILE_PAD_H))
  const width = Math.ceil(font * 0.65 * value.length) + padding * 2
  const height = Math.ceil(font * 1.2) + Math.max(1, Math.round(font * TILE_PAD_TOP)) + Math.max(1, Math.round(font * TILE_PAD_BOTTOM))
  return { font, width, height, radius: Math.max(2, Math.round(font * TILE_RADIUS)) }
}

function nativeDie(value: string, diceSize: number, color: string, key: number, x = 0, nested = false, fontOverride?: number) {
  const { font, width, height, radius } = nativeDieGeometry(value, diceSize, fontOverride)
  const Container = nested ? G : Svg
  const placement = nested ? { transform: [{ translateX: x }] } : { width, height }
  return (
    <Container key={key} {...placement} accessibilityLabel={`Die ${value}`}>
      <Rect width={width} height={height} rx={radius} fill={DIE_TILE_FILL} />
      <SvgText x={width / 2} y={height / 2 + font * 0.36} textAnchor="middle" fontFamily="Inter" fontSize={font} fill={color}>
        {value}
      </SvgText>
    </Container>
  )
}

function nativeDiceComparison(attacker: string, defender: string, diceSize: number, color: string, surroundingFontSize: number, key: number, fontOverride?: number) {
  const size = surroundingFontSize * PixelRatio.getFontScale()
  const height = nativeDieGeometry('1', diceSize, fontOverride).height
  const gap = Math.max(2, Math.round(size * 0.2))
  const vsWidth = Math.ceil(size * 1.5)
  let x = 0
  let tileKey = 0
  const children: React.ReactNode[] = []
  const addDice = (text: string) => {
    const faces = Array.from(text.matchAll(/\[(\d+)\]/g), match => match[1]!)
    faces.forEach((value, index) => {
      if (index > 0) x += gap
      children.push(nativeDie(value, diceSize, color, tileKey++, x, true, fontOverride))
      x += nativeDieGeometry(value, diceSize, fontOverride).width
    })
  }
  addDice(attacker)
  children.push(
    <SvgText key="vs" x={x + vsWidth / 2} y={height / 2 + size * 0.24} textAnchor="middle" fontFamily="Zilla Slab" fontSize={size} fill={color}>
      vs
    </SvgText>,
  )
  x += vsWidth
  addDice(defender)
  return <Svg key={key} width={x} height={height} accessibilityLabel={`Roll ${attacker.trim()} vs ${defender.trim()}`}>{children}</Svg>
}

/** A normal layout item, sized to the surrounding native log typography. */
export function renderNativeDiceComparison(attacker: string, defender: string, fontSize: number, color: string) {
  const numeralSize = Math.max(8, Math.round(fontSize * PixelRatio.getFontScale() * 0.8))
  return nativeDiceComparison(attacker, defender, 0, color, fontSize, 0, numeralSize)
}

/**
 * Turn a formatted log line into React children: each `[n]` die token becomes a
 * numeral tile sized off `diceSize`. Everything else is returned as plain strings —
 * React Native happily mixes strings and Text children inside a parent <Text>.
 *
 * If `highlights` is provided, each entry's text is wrapped in a coloured <Text> the
 * FIRST time it appears in the line (in the order given), before the dice pass. Used
 * to colour attacker + defender display names in combat rows so the by-player
 * association is visible inline. Highlights are skipped if `text` is empty (e.g. a
 * neutral defender).
 */
export function renderLogText(
  text: string,
  diceSize: number,
  highlights?: NameHighlight[],
  diceColor = '#18170f',
  surroundingFontSize = 14,
): React.ReactNode {
  let key = 0
  // First pass: split the input string into a mixed array of strings and
  // colour-wrapped Text nodes by walking the highlights in order.
  // Each highlight matches its first occurrence in the longest remaining
  // string segment, splitting it into [before, coloured, after].
  let segments: React.ReactNode[] = [text]
  if (highlights && highlights.length > 0) {
    for (const h of highlights) {
      if (!h.text) continue
      const next: React.ReactNode[] = []
      let consumed = false
      for (const seg of segments) {
        if (consumed || typeof seg !== 'string') {
          next.push(seg)
          continue
        }
        const idx = seg.indexOf(h.text)
        if (idx === -1) {
          next.push(seg)
          continue
        }
        if (idx > 0) next.push(seg.slice(0, idx))
        next.push(
          <Text key={key++} style={{ color: h.color, fontWeight: '700' }}>
            {h.text}
          </Text>,
        )
        const tail = seg.slice(idx + h.text.length)
        if (tail.length > 0) next.push(tail)
        consumed = true
      }
      segments = next
    }
  }

  // Second pass: turn every die token into a tile, in each remaining string
  // segment, so dice render the same whether or not the line also has coloured
  // player names in it.
  const parts: React.ReactNode[] = []
  for (const seg of segments) {
    if (typeof seg !== 'string') {
      parts.push(seg)
      continue
    }
    let lastIndex = 0
    let match: RegExpExecArray | null
    const font = Math.max(8, Math.round(diceSize * TILE_FONT))
    LOG_DIE_RE.lastIndex = 0
    while ((match = LOG_DIE_RE.exec(seg)) !== null) {
      const adjacent = match.index === lastIndex
      if (!adjacent) {
        parts.push(seg.slice(lastIndex, match.index))
      } else if (lastIndex > 0) {
        // Two tokens back to back: their fills would touch and read as one long
        // rounded pill instead of two dice.
        parts.push(TILE_GAP)
      }
      if (Platform.OS !== 'web') {
        // Keep the dice and "vs" in one attachment. iOS and Android position
        // inline attachments differently relative to surrounding text; drawing
        // the whole comparison gives them a shared center on both platforms.
        const comparison = seg.slice(match.index).match(/^((?:\[\d+\][ \t]*)+)vs[ \t]+((?:\[\d+\][ \t]*)+)/)
        if (comparison) {
          parts.push(nativeDiceComparison(comparison[1]!, comparison[2]!, diceSize, diceColor, surroundingFontSize, key++))
          lastIndex = match.index + comparison[0].length
          // Preserve the whitespace after the comparison for the loss summary.
          const trailing = comparison[0].match(/[ \t]+$/)?.[0]
          if (trailing) parts.push(trailing)
          LOG_DIE_RE.lastIndex = lastIndex
        } else {
          parts.push(nativeDie(match[1]!, diceSize, diceColor, key++))
          lastIndex = match.index + match[0].length
        }
        continue
      }
      parts.push(
        <Text
          key={key++}
          style={{
            fontSize: font,
            lineHeight: diceSize,
            // Every die is a tile now, so they align with each other by
            // construction; `middle` is then the natural inline-badge alignment
            // against the surrounding text. (The old percentage nudge existed to
            // fix the pip glyphs' odd metrics and went with them.)
            verticalAlign: 'middle',
            fontWeight: '700',
            // NO textAlign here. `textAlign` is a PARAGRAPH attribute on iOS, and
            // iOS applies a paragraph attribute set on any range to the WHOLE
            // paragraph — so 'center' on a one-character die tile centred the
            // entire log line it sat in. That is why every row carrying dice was
            // centre-aligned on device while web (where each tile is its own
            // inline span) stayed left-aligned. A tile holds a single digit and
            // is sized to it, so there is nothing to centre anyway.
            // LINING figures so every face sits at one height, TABULAR so every
            // tile comes out the same width. The log's face (Zilla Slab) defaults
            // to OLD-STYLE figures: per 1000 units/em its 3 4 5 7 9 descend to
            // about -140 while 6 and 8 rise to 660 and 0 1 2 stop at x-height, so
            // a 7 hangs below its neighbours until `lnum` picks the uniform set.
            fontVariant: ['lining-nums', 'tabular-nums'],
            backgroundColor: DIE_TILE_FILL,
            borderRadius: Math.max(2, Math.round(font * TILE_RADIUS)),
            paddingHorizontal: Math.max(2, Math.round(font * TILE_PAD_H)),
            // Asymmetric on purpose, from this face's own metrics — see
            // TILE_PAD_BOTTOM.
            paddingTop: Math.max(1, Math.round(font * TILE_PAD_TOP)),
            paddingBottom: Math.max(1, Math.round(font * TILE_PAD_BOTTOM)),
          }}
        >
          {match[1]}
        </Text>,
      )
      lastIndex = match.index + match[0].length
    }
    if (lastIndex < seg.length) parts.push(seg.slice(lastIndex))
  }
  if (parts.length === 0) return text
  if (parts.length === 1 && typeof parts[0] === 'string') return parts[0]
  return parts
}
